import { useState } from 'react';
import { motion } from 'framer-motion';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  FlaskConical,
  Plus,
  BarChart3,
  Beaker,
  ChevronLeft,
  RefreshCw,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/stores/authStore';
import { useABTests, ABTestStatus } from '@/hooks/useABTests';
import { ABTestCard } from '@/components/abtesting/ABTestCard';
import { ABTestAnalytics } from '@/components/abtesting/ABTestAnalytics';
import { CreateABTestModal } from '@/components/abtesting/CreateABTestModal';
import { DeclareWinnerModal } from '@/components/abtesting/DeclareWinnerModal';

export default function ABTesting() {
  const { user, isLoading: authLoading } = useAuthStore();
  const {
    tests,
    isLoading,
    updateTestStatus,
    declareWinner,
    deleteTest,
  } = useABTests();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTestId = searchParams.get('test');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [winnerModalTestId, setWinnerModalTestId] = useState<string | null>(null);

  if (authLoading || isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const selectedTest = tests.find((t) => t.id === selectedTestId);
  const activeTests = tests.filter((t) => t.status === 'active');
  const draftTests = tests.filter((t) => t.status === 'draft');
  const completedTests = tests.filter((t) => t.status === 'completed' || t.status === 'paused');

  const handleViewAnalytics = (testId: string) => {
    setSearchParams({ test: testId });
  };

  const handleBackToList = () => {
    setSearchParams({});
  };

  const handleDeclareWinner = async (variantName: string) => {
    if (!winnerModalTestId) return;
    await declareWinner(winnerModalTestId, variantName);
    setWinnerModalTestId(null);
  };

  // Analytics view for selected test
  if (selectedTest) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToList}
              className="mb-4"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Tests
            </Button>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
                  <FlaskConical className="h-8 w-8 text-primary" />
                  {selectedTest.name}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {selectedTest.testType === 'design' ? 'Design' : 'Destination'} Test
                </p>
              </div>
              <div className="flex items-center gap-2">
                {(selectedTest.status === 'active' || selectedTest.status === 'paused') && (
                  <Button
                    variant="outline"
                    onClick={() => setWinnerModalTestId(selectedTest.id)}
                  >
                    Declare Winner
                  </Button>
                )}
              </div>
            </div>
          </motion.div>

          <ABTestAnalytics
            test={selectedTest}
            onDeclareWinner={(variantName) => declareWinner(selectedTest.id, variantName)}
          />
        </div>

        <DeclareWinnerModal
          test={selectedTest}
          open={winnerModalTestId === selectedTest.id}
          onOpenChange={(open) => !open && setWinnerModalTestId(null)}
          onConfirm={handleDeclareWinner}
        />
      </Layout>
    );
  }

  // Test list view
  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <FlaskConical className="h-8 w-8 text-primary" />
                A/B Testing
              </h1>
              <p className="text-muted-foreground mt-1">
                Test different QR designs and destinations to optimize performance
              </p>
            </div>
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Test
            </Button>
          </div>
        </motion.div>

        {tests.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-16 text-center"
          >
            <div className="p-6 rounded-full bg-primary/10 mb-6">
              <Beaker className="h-12 w-12 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">No A/B Tests Yet</h2>
            <p className="text-muted-foreground max-w-md mb-6">
              Create your first A/B test to compare different QR code designs or
              destination URLs and find what works best.
            </p>
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Test
            </Button>
          </motion.div>
        ) : (
          <Tabs defaultValue="active" className="space-y-6">
            <TabsList>
              <TabsTrigger value="active" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Active ({activeTests.length})
              </TabsTrigger>
              <TabsTrigger value="draft" className="flex items-center gap-2">
                <Beaker className="h-4 w-4" />
                Drafts ({draftTests.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="flex items-center gap-2">
                Completed ({completedTests.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-4">
              {activeTests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No active tests. Start a draft test to begin collecting data.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {activeTests.map((test) => (
                    <ABTestCard
                      key={test.id}
                      test={test}
                      onViewAnalytics={handleViewAnalytics}
                      onUpdateStatus={updateTestStatus}
                      onDeclareWinner={() => setWinnerModalTestId(test.id)}
                      onDelete={deleteTest}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="draft" className="space-y-4">
              {draftTests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No draft tests. Create a new test to get started.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {draftTests.map((test) => (
                    <ABTestCard
                      key={test.id}
                      test={test}
                      onViewAnalytics={handleViewAnalytics}
                      onUpdateStatus={updateTestStatus}
                      onDeclareWinner={() => setWinnerModalTestId(test.id)}
                      onDelete={deleteTest}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="completed" className="space-y-4">
              {completedTests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No completed tests yet.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {completedTests.map((test) => (
                    <ABTestCard
                      key={test.id}
                      test={test}
                      onViewAnalytics={handleViewAnalytics}
                      onUpdateStatus={updateTestStatus}
                      onDeclareWinner={() => setWinnerModalTestId(test.id)}
                      onDelete={deleteTest}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      <CreateABTestModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />

      {winnerModalTestId && (
        <DeclareWinnerModal
          test={tests.find((t) => t.id === winnerModalTestId)!}
          open={!!winnerModalTestId}
          onOpenChange={(open) => !open && setWinnerModalTestId(null)}
          onConfirm={handleDeclareWinner}
        />
      )}
    </Layout>
  );
}
